import React, { useMemo } from 'react';
import cx from 'classnames';
import styles from 'js/components/pipelines/cards/PipelineOriginCard.module.scss';
import Typography from 'new/elements/TypographySupport/TypographySupport';
import { ReactComponent as ArrowIcon } from 'new/components/icons/arrow.svg';
import { ReactComponent as ArrowRightIcon } from 'new/components/icons/longArrowRight.svg';
import useEntities from 'new/state/entities/repository/useEntities';
import { getResourceEntity } from 'js/lib/PipelineUtils';
import { getFlowSinkIds } from 'js/actions/rag';

interface RAGCountersProps {
  isClickablePartHovered?: boolean;
  data: any;
  hideArrow?: boolean;
}

const names = {
  query_rewriter: 'Query Rewriter',
  data_retriever_holder: 'Data Retriever',
  context_enricher: 'Context Enricher',
  llm_processor: 'LLM Processor',
};

function buildPipelineComponents(data, entities) {
  const components = [];
  let currentData = data;

  while (currentData && currentData.children?.length > 0) {
    const nextData = getResourceEntity(entities.flowNodes[currentData.children[0]], entities, true);

    if (!nextData) break;

    const isRetriever = nextData?.node_type === 'data_retriever_holder';
    const isLLM = nextData?.node_type === 'llm_processor';
    const flow = entities.flowNodes[nextData?.origin_node_id];
    const retrieversCount = isRetriever ? flow?.linked_flows?.length || 0 : undefined;
    const llmSinkIds = getFlowSinkIds(flow, entities.flowNodes);
    const llmCount = isLLM ? llmSinkIds.length : undefined;

    const count = retrieversCount ?? llmCount ?? 1;

    components.push({
      nodeType: nextData.node_type,
      count,
      name: names?.[nextData.node_type],
    });

    currentData = nextData;
  }

  const hasLLMProcessor = components.some((comp) => comp.nodeType === 'llm_processor');
  if (!hasLLMProcessor) {
    components.push({
      nodeType: 'llm_processor',
      count: 0,
      name: names?.llm_processor,
    });
  }

  return components;
}

export const RAGCounters = (props: RAGCountersProps) => {
  const { isClickablePartHovered, data } = props;
  const { data: entities } = useEntities();

  const Components = useMemo(() => {
    const components = buildPipelineComponents(data, entities);

    return components.map((component, index) => (
      <React.Fragment key={component.nodeType}>
        <div>
          <div className={styles.circle}>{component.count}</div>
          <Typography variant="caption" color="primary.dark">
            {component.name}
            {component.count === 1 ? '' : 's'}
          </Typography>
        </div>
        {index < components.length - 1 && <ArrowRightIcon />}
      </React.Fragment>
    ));
  }, [data, entities]);

  return (
    <div className={cx(styles.counters, isClickablePartHovered && styles.countersHovered)}>
      {!props.hideArrow && <ArrowIcon className={styles.arrowOpen} />}
      {Components}
    </div>
  );
};
